import { ActionIcon, Card, CardProps } from "@mantine/core";
import { Link } from "../ui/Link";
import { useState, useCallback, useMemo } from "react";
import { useStyles } from "@/styles/useStyles";
import { Badge } from "../ui/Badge";
import { IconExternalLink } from "@tabler/icons-react";
import { Analytics } from "@/services/Analytics";

export function UserProfileCard(
  props: CardProps & {
    children: React.ReactNode;
    href: string;
    badge?: {
      color: string;
      label: string;
      leftSection?: React.ReactNode;
      variant?: "filled" | "light";
    };
  },
) {
  const { badge, href } = props;
  const [isHovered, setIsHovered] = useState(false);
  const { theme } = useStyles();

  // Extract resource type from href (e.g., "/advizers/123" -> "advizers")
  const resourceType = useMemo(() => {
    const pathParts = href.split("?")[0].split("/").filter(Boolean);
    return pathParts[0] || "unknown";
  }, [href]);

  const handleLinkClick = useCallback(
    (event: any) => {
      Analytics.click({
        context: "User profile",
        action: `User profile card > ${resourceType}`,
        label: href,
        event,
      });
    },
    [href, resourceType],
  );

  return (
    <Link
      to={props.href as any}
      target="_blank"
      className="userProfileCard"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleLinkClick}
    >
      <Card
        {...props}
        shadow={isHovered ? "sm" : "xs"}
        pt="lg"
        pb="lg"
        // bg={isHovered ? "primary.0" : "background.0"}
        style={{
          transition: "box-shadow 0.2s ease background-color 0.5s ease",
          border: isHovered
            ? `1px solid ${theme.colors.border[3]}`
            : `1px solid transparent`,
        }}
      >
        {props.children}
        {href && (
          <ActionIcon
            variant="transparent"
            size="xs"
            pos="absolute"
            top={4}
            right={4}
            opacity={0.5}
          >
            <IconExternalLink size={16} />
          </ActionIcon>
        )}
        {badge ? (
          <Badge
            color={badge.color}
            size="lg"
            radius="sm"
            ariaLabel={badge.label}
            pos="absolute"
            bottom={0}
            right={0}
            style={{ borderBottomLeftRadius: 0, borderTopRightRadius: 0 }}
            leftSection={badge.leftSection}
            variant={badge.variant || "light"}
          >
            {badge.label}
          </Badge>
        ) : null}
      </Card>
    </Link>
  );
}
